import { Dialog, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React, { useEffect, useState } from "react";
import baseURL from "../../../configs/api";
import { AddNewCustomer } from "./AddNewCustomer2";
import CustomerSelectWrapper from "./CustomerSelectWrapper";
import useConfirm from "../../../components/hooks/useConfirm";
import { useTheme } from "../../../components/hooks/useTheme";
import useAuth from "../../../components/hooks/useAuth";
import { driverChange, driverChangeFromExistingDriver } from "../../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";

type Props = {
  poupType?: boolean;
  addNewDiver: boolean;
  setAddNewDriver: (value: boolean) => void;
  customerId: number;
  setCustomerId: (value: number) => void;
  drivers: any[];
  driverId: number;
  isPrimaryDriver: boolean;
  setIsPrimaryDriver: (value: boolean) => void;
  handlePleaseWait: (pleaseWaitPopup: boolean) => void;
  refreshTheSummary: () => void;
  pickupDate: string;
  returnDate: string;
  returnTime: string;
  preferredDateFormat: string;
  selectedVehicleClass: any;
  reservationId: number;

};

export default function AddDriver({
  poupType = true,
  addNewDiver,
  setAddNewDriver,
  customerId,
  setCustomerId,
  drivers,
  driverId,
  isPrimaryDriver,
  setIsPrimaryDriver,
  handlePleaseWait,
  refreshTheSummary,
  pickupDate,
  returnDate,
  returnTime,
  preferredDateFormat,
  selectedVehicleClass,
  reservationId,
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { theme, themeStyles } = useTheme();
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [selectedOption, setSelectedOption] = useState<any>();
  const [customerKey, setCustomerKey] = useState(0);
  const [options, setOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isNextPageLoading, setNextPageLoading] = useState(false);
  const [pageNo, setPage] = useState(-1);
  const [searchValue, setSearchValue] = useState("");
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  useEffect(() => {
    if (customerId <= 0) {
      setSelectedOption(null);
    } else {
      const existingDriver = drivers?.find((item: any) => item?.customerId === customerId);
      setSelectedOption({
        value: customerId,
        label: existingDriver?.customerName
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addNewDiver])

  const calculateAge = (date: string) => {
    var today = new Date();
    var birthDate = new Date(date);
    var age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleCreateNew = () => {
    setSelectedOption(null);
    setCustomerId(0);
    setCustomerKey((prevKey) => prevKey + 1);
  };

  useEffect(() => {
    let timeOutId: NodeJS.Timeout;
    setHasNextPage(true);
    setNextPageLoading(false);
    setPage(-1);
    if (searchValue !== "") {
      timeOutId = setTimeout(() => {
        setIsSearching(false);
        setOptions([]);
      }, 1000);
    } else {
      setOptions([]);
      setIsSearching(false);
    }

    return () => clearTimeout(timeOutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const loadNextPage = () => {
    if (searchValue === "") {
      loadWithOutSearchOptions(pageNo + 1);
    } else {
      loadWithSearchOptions(pageNo + 1);
    }
  };

  const loadWithOutSearchOptions = (page: number) => {
    try {
      const pagination = {
        page: page,
        size: 15,
      };
      setNextPageLoading(true);
      fetch(`${baseURL}Customer/GetCustomersPaging/${pagination.page},${pagination.size}`, {
  method: 'GET',
        headers: {
          'X-Tenant-ID': tenantId?.toString(),
          'Content-Type': 'application/json'
        }
      })
        .then((res) => res.json())
        .then((data) => {
          const dataOptions = data?.customers?.map((item: any) => {
            return {
              value: item.id,
              label: item.firstName + " " + item.lastName,
            };
          });

          const itemsData = options.concat(dataOptions);
          setOptions(itemsData);
          setNextPageLoading(false);
          setHasNextPage(itemsData.length < data.count);
          setPage(page);
          handleTenantError(null);
        })
        .catch((error) => {
          handleTenantError(error);
        });
    } catch (error) {
      handleTenantError(error);
    }
  };

  const loadWithSearchOptions = (page: number) => {
    try {
      const pagination = {
        page: page,
        size: 15,
      };
      setNextPageLoading(true);
      fetch(`${baseURL}Customer/GetCustomersSearchWithCustomerNamePaging?customerName=${searchValue}&page=${pagination.page}&size=${pagination.size}`, {
  method: 'GET',
        headers: {
          'X-Tenant-ID': tenantId?.toString(),
          'Content-Type': 'application/json'
        }
      })
        .then((res) => res.json())
        .then((data) => {
          const dataOptions = data?.customers?.map((item: any) => {
            return {
              value: item.id,
              label: item.firstName + " " + item.lastName,
            };
          });

          const itemsData = options.concat(dataOptions);
          setOptions(itemsData);
          setNextPageLoading(false);
          setHasNextPage(itemsData.length < data.count);
          setPage(page);
          handleTenantError(null);
        })
        .catch((error) => {
         handleTenantError(error);
        });
    } catch (error) {
      handleTenantError(error);
    }
  };

  const updateTheDriver = (updateCustomerId: number, newCustomer?: any) => {
    handlePleaseWait(true);
    setCustomerId(updateCustomerId);

    let updateCustomer: any[] = options;

    if (updateCustomer?.find((item: any) => item?.value === updateCustomerId)) {
      updateCustomer = updateCustomer.map((item) => {
        if (item.value === updateCustomerId) {
          return newCustomer;
        }
        return item;
      });
    } else {
      if (newCustomer) {
        updateCustomer = [
          ...updateCustomer,
          newCustomer
        ];
      }
    }

    const customer = updateCustomer?.find((item) => item.value === updateCustomerId);
    if (customer) {
      const customerDob = new Date(customer?.dob);
      const customerDobString = customerDob?.toISOString().split('T')[0];

      const customerAge = calculateAge(customerDobString);

      if (
        !customerAge ||
        (customerAge < selectedVehicleClass?.minimumRentalAllowedAge ||
          customerAge > selectedVehicleClass?.maxRentalAllowedAge)
      ) {
        setConfirmData({
          title: 'Warning',
          summary: "Your age does not meet the requirements to operate this vehicle.",
          confirmBtn: 'Ok',
          isConfirm: (callback: any) => {
            callback();
          }
        });
        setConfirmPopup(true);
        handlePleaseWait(false);
        return;
      }
    } else {
      setConfirmData({
        title: 'Warning',
        summary: "Please add date of birth of customer.",
        confirmBtn: 'Ok',
        isConfirm: (callback: any) => {
          callback();
        }
      });
      setConfirmPopup(true);
      handlePleaseWait(false);
      return;
    }

    setAddNewDriver(false)
    handlePleaseWait(true);

    if (driverId === 0) {
      const existingDriver = drivers?.find((item: any) => item?.customerId === updateCustomerId);

      if (existingDriver) {
        setConfirmData({
          title: 'Warning',
          summary: "Customer is already added as Driver!!",
          confirmBtn: 'Ok',
          isConfirm: (callback: any) => {
            callback();
          }
        });

        setConfirmPopup(true);
        handlePleaseWait(false);
        return;
      }

      driverChange(
        reservationId,
        updateCustomerId,
        isPrimaryDriver,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
        })
        .catch((error) => {
          console.log("Error:", error);
          handlePleaseWait(false);
        });
    } else {
      driverChangeFromExistingDriver(
        reservationId,
        driverId,
        updateCustomerId,
        isPrimaryDriver,
        auth?.id,
        tenantId
      )
        .then(() => {
          refreshTheSummary();
          handleTenantError(null);
        })
        .catch((error) => {
          handleTenantError(error);
          handlePleaseWait(false);
        })
    }
  }


  return (
    <Dialog
      open={addNewDiver}
      maxWidth="md"
      fullScreen={false}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '800px',
          width: '100%',
          borderRadius: '16px',
        },
      }}
    >
      <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320.591 320.591"
          className={`w-3 cursor-pointer float-right ${closeIconFill}`}
          onClick={() => setAddNewDriver(false)}
        >
          <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
          <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
        </svg>
        <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
          <div className="w-full mt-2">
            <div className={`w-full`}>
              <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                <div className="flex items-center gap-1.5">
                  <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>{!poupType ? "Edit Driver" : "Add Driver"}</h2>
                </div>
              </div>

              <div className="mt-2 p-2">
                <div className="w-full">
                  <div className="w-full relative flex gap-2.5">
                    <div className="w-[70%]">
                      <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Existing Driver</label>
                      <CustomerSelectWrapper
                        key={customerKey}
                        hasNextPage={hasNextPage}
                        isNextPageLoading={isNextPageLoading}
                        setSearchValue={setSearchValue}
                        options={options}
                        selectedOption={selectedOption}
                        setPage={setPage}
                        loadNextPage={loadNextPage}
                        isSearching={isSearching}
                        setIsSearching={setIsSearching}
                        onChange={(selectedOption: any) => {
                          setCustomerId(selectedOption?.value)
                          setSelectedOption(selectedOption);
                        }}
                      />
                    </div>

                    <span className={`mt-[30px] text-[14px] font-semibold text-[#167AC3] cursor-pointer`}
                      onClick={handleCreateNew}
                    >
                      or create a new one
                    </span>
                  </div>

                  <div className="w-full mt-4">
                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Primary Driver?</label>
                    <RadioGroup
                      row
                      value={isPrimaryDriver ? "Yes" : "No"}
                      onChange={(e) => setIsPrimaryDriver(e.target.value === "Yes" ? true : false)}
                    >
                      <FormControlLabel
                        value="Yes"
                        control={
                          <Radio
                            sx={{
                              color: "gray",
                              "&.Mui-checked": {
                                color: "#167AC3",
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 14,
                              },
                            }}
                          />
                        }
                        label="Yes"
                        sx={{
                          color: themeStyles.form.labelTextColor
                        }}
                      />

                      <FormControlLabel
                        value="No"
                        control={
                          <Radio
                            sx={{
                              color: "gray",
                              "&.Mui-checked": {
                                color: "#167AC3",
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 14,
                              },
                            }}
                          />
                        }
                        label="No"
                        sx={{
                          color: themeStyles.form.labelTextColor
                        }}
                      />
                    </RadioGroup>
                  </div>
                </div>
              </div>
            </div>

            <AddNewCustomer
              handleCustomer={(updateCustomerId, newCustomer) =>
                updateTheDriver(updateCustomerId, newCustomer)
              }
              closeNewCustomer={() => setAddNewDriver(false)}
              customerId={customerId}
              isFromDriver={true}
              pickupDate={pickupDate}
              returnDate={returnDate}
              returnTime={returnTime}            
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
